import {
  APInvoiceStatus,
  MatchConfidenceStatus,
  TroubleType,
} from '@erp/shared-types';
import type { TroubleData } from '@erp/shared-types';
import type { EntityAssociationMatchMetadata } from '@erp/rpc';
import type {
  PurchaseOrderLineItem,
  SalesOrderLineItem,
  Charge,
} from '@erp/rpc';
import {
  hasActiveTrouble,
  hasActiveTroubleValue,
} from '@/components/common/TroubleDataBadges';

/** Must match backend HIGH_CONFIDENCE_THRESHOLD in accounts-payable/constants.ts */
const HIGH_CONFIDENCE_THRESHOLD = 0.95;

export type MatchDisplayState =
  | 'DRAFT'
  | 'NOT_READY'
  | 'REVIEW'
  | 'MATCHED'
  | 'ACCEPTED'
  | 'FINALIZED'
  | 'VOID';

/** Badge text keyed by display state (states are not valid display strings). */
export const DISPLAY_STATE_LABELS: Record<MatchDisplayState, string> = {
  DRAFT: 'DRAFT',
  NOT_READY: 'NOT READY',
  REVIEW: 'REVIEW',
  MATCHED: 'MATCHED',
  ACCEPTED: 'ACCEPTED',
  FINALIZED: 'FINALIZED',
  VOID: 'VOID',
};

interface MatchSummary {
  confirmed: number;
  tentative: number;
  unmatched: number;
  total: number;
}

/**
 * Derive the user-facing display state from invoice status + match summary +
 * header trouble data.
 *
 *  DRAFT        → no matches at all (red)
 *  NOT_READY    → matching paused: a matched order isn't ready in the source
 *                 ERP (active ORDER_NOT_READY trouble). Supersedes DRAFT /
 *                 REVIEW, but never MATCHED — a fully-matched invoice means
 *                 the trouble flag is stale.
 *  REVIEW       → has tentative matches needing confirmation (yellow)
 *  MATCHED      → all line items confirmed (green)
 *  FINALIZED / VOID → passthrough from DB status
 */
export function getMatchDisplayState({
  status,
  summary,
  troubleData,
}: {
  status: string;
  summary: MatchSummary | null | undefined;
  troubleData?: TroubleData | null;
}): MatchDisplayState {
  if (status === APInvoiceStatus.MATCHED) return 'MATCHED';
  if (status === APInvoiceStatus.ACCEPTED) return 'ACCEPTED';
  if (status === APInvoiceStatus.FINALIZED) return 'FINALIZED';
  if (status === APInvoiceStatus.VOID) return 'VOID';
  // DRAFT status: all line items confirmed → matched display (any trouble
  // entry still hanging around is stale at that point).
  if (summary && summary.total > 0 && summary.confirmed === summary.total) {
    return 'MATCHED';
  }
  if (hasActiveTrouble(troubleData, TroubleType.ORDER_NOT_READY)) {
    return 'NOT_READY';
  }
  if (!summary || summary.total === 0) return 'DRAFT';
  if (summary.tentative > 0) return 'REVIEW';
  return 'DRAFT';
}

/**
 * Classify a match as tentative based on metadata.
 * Mirrors backend classifyMatchStatus logic — checks locked-in matchStatus
 * first, falls back to confidence threshold for legacy records.
 */
export function isTentativeMatch(
  metadata: EntityAssociationMatchMetadata | null | undefined,
): boolean {
  if (!metadata) return false;
  if (metadata.matchStatus === MatchConfidenceStatus.CONFIRMED) return false;
  if (metadata.matchStatus === MatchConfidenceStatus.TENTATIVE) return true;
  // Legacy fallback: no matchStatus, derive from confidence
  if (metadata.confidence === undefined) return false;
  return metadata.confidence < HIGH_CONFIDENCE_THRESHOLD;
}

/**
 * Whether a match was created manually by a user (vs. produced automatically
 * by reconciliation or the AI agent). Mirrors the backend convention in
 * `classifyMatchStatus`: automatic matches always persist metadata
 * (confidence / matchMethod / matchStatus), while a manual match carries none.
 */
export function isManualMatch(
  metadata: EntityAssociationMatchMetadata | null | undefined,
): boolean {
  return metadata == null || Object.keys(metadata).length === 0;
}

export function getActiveHoldPay(
  matchedPOLine?: PurchaseOrderLineItem | null,
  matchedSOLine?: SalesOrderLineItem | null,
  matchedCharge?: Charge | null,
): boolean {
  return (
    !!matchedPOLine?.paymentHold ||
    hasActiveTroubleValue(matchedPOLine?.troubleData, 'HOLD_PAY') ||
    hasActiveTroubleValue(matchedSOLine?.troubleData, 'HOLD_PAY') ||
    hasActiveTroubleValue(matchedCharge?.troubleData, 'HOLD_PAY')
  );
}

export function getActiveConsign(
  matchedSOLine?: SalesOrderLineItem | null,
): boolean {
  return (
    !!matchedSOLine?.isConsignment ||
    hasActiveTroubleValue(matchedSOLine?.troubleData, 'CONSIGN')
  );
}

/** Badge color classes keyed by display state. */
export const DISPLAY_STATE_BADGE_CLASSES: Record<MatchDisplayState, string> = {
  DRAFT: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
  // Matches the TroubleDataBadges ORDER_NOT_READY palette
  NOT_READY: 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100',
  REVIEW: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200',
  MATCHED: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
  ACCEPTED: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
  FINALIZED: 'bg-gray-900 text-white hover:bg-gray-800',
  VOID: 'bg-gray-100 text-gray-600 border-gray-200',
};
